import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import { useEffect, useRef } from "react";

/**
 * DotsGraph component renders a scatter/dot plot with D3 inside React.
 * @memberof module:components/graphs
 * @param {Object} props
 * @param {number} props.width - Width of the SVG container.
 * @param {number} props.height - Height of the SVG container.
 * @param {Object} props.defaultOptiopns - Default options for the graph (typo in name preserved).
 * @param {Array} props.defaultOptiopns.thresholds - Array of threshold objects {label, value, color}.
 * @param {number} props.defaultOptiopns.yMinValue - Minimum y-axis value.
 * @param {number} props.defaultOptiopns.yMaxValue - Maximum y-axis value.
 * @param {string} props.defaultOptiopns.xLabelName - Label for the x-axis.
 * @param {string} props.defaultOptiopns.yLabelName - Label for the y-axis.
 * @param {number} props.defaultOptiopns.yTicks - Number of ticks on y-axis.
 * @param {string} props.defaultOptiopns.title - Title of the graph.
 * @param {Object} props.intervalOptions - Options for x-axis time range.
 * @param {string|Date} props.intervalOptions.startDate - Start date of the interval.
 * @param {string|Date} props.intervalOptions.stopDate - End date of the interval.
 * @param {Array<Object>} props.dataset - Array of data points with at least timestamp, frequency, bearing properties.
 *
 * @returns {JSX.Element}
 */
const AreaGraph = ({
  width,
  height,
  defaultOptiopns,
  areaOptions,
  intervalOptions,
  dataset,
}) => {
  const room_for_axis = 50;
  const room_for_legend = defaultOptiopns?.legendValues?.length > 0 ? 25 : 0;
  const margin = 70;
  const innerWidth = width - margin;
  const innerHeight = height - margin + room_for_legend;

  const offsetTimeAxis =
    (intervalOptions.stopDate - intervalOptions.startDate) * 0.1;

  const xAxisLower =
    dataset?.length > 0 && dataset[0].timestamp < intervalOptions.startDate
      ? dataset[0].timestamp - offsetTimeAxis
      : intervalOptions.startDate - offsetTimeAxis;
  const xAxisUpper =
    dataset?.length > 0 &&
    dataset[dataset.length - 1].timestamp > intervalOptions.stopDate
      ? dataset[dataset.length - 1].timestamp + offsetTimeAxis
      : intervalOptions.stopDate + offsetTimeAxis;

  const ref = useRef();
  var svg;
  var g;
  var xScale, yScale;
  var rect;
  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();
    draw();
  }, [dataset, intervalOptions]);

  const splitByTimeGaps = (data) => {
    if (!data || data.length === 0) return [];

    const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);

    const diffs = d3.pairs(sorted, (a, b) => b.timestamp - a.timestamp);
    if (diffs.length === 0) return [sorted];

    const maxGap = d3.max(diffs);

    const MIN_GAP = 30000;
    const GAP_RATIO = 0.1;

    const threshold = Math.max(maxGap * GAP_RATIO, MIN_GAP);

    const segments = [];
    let current = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];

      const diff = curr.timestamp - prev.timestamp;

      if (diff >= threshold) {
        segments.push(current);
        current = [];
      }

      current.push(curr);
    }

    if (current.length) segments.push(current);

    return segments;
  };

  const drawAreas = () => {
    if (!areaOptions || areaOptions?.length === 0) return;

    g.selectAll(".area-band")
      .data(areaOptions)
      .enter()
      .append("rect")
      .attr("class", "area-band")
      .attr("x", (d) => xScale(new Date(d.timestamp)))
      .attr("width", (d) => {
        const start = new Date(d.timestamp);
        const end = new Date(d.timestamp + d.value * 1000); // seconds → ms

        return Math.max(1, xScale(end) - xScale(start));
      })
      .attr("y", 0)
      .attr("height", innerHeight)
      .attr("fill", (d) => d.color || "orange")
      .attr("opacity", 0.4);
  };

  /**
   * Draws the legend to the right side of the graph showing thresholds.
   */
  const drawLegend = () => {
    // Draw legend (static)
    const legend = svg.append("g").attr("id", "legends");
    defaultOptiopns?.legend?.forEach((element, index) => {
      const xPos =
        room_for_axis + (innerWidth / defaultOptiopns.legend.length) * index;
      legend
        .append("rect")
        .attr("x", xPos)
        .attr("y", height - room_for_legend)
        .attr("width", 16)
        .attr("height", 16)
        .attr("stroke", "black")
        .attr("fill", element.color);

      legend
        .append("text")
        .attr("x", xPos + 20)
        .attr("y", height - room_for_legend + 8)
        .attr("font-size", 14)
        .attr("alignment-baseline", "middle")
        .attr("fill", "white")
        .text(element.label);
    });
  };

  /**
   * Draws X (time) and Y (linear) axes with labels.
   */
  const drawTimeAxis = () => {

    //Axis
    xScale = d3
      .scaleUtc()
      .domain([new Date(xAxisLower), new Date(xAxisUpper)])
      .range([0, innerWidth]);

    g = svg
      .append("g")
      .attr("class", "chart_view")
      .attr("transform", "translate(" + 45 + "," + 35 + ")");

    var start = new Date(intervalOptions.startDate);
    var end = new Date(intervalOptions.stopDate);

    var diffDays = (end - start) / (1000 * 60 * 60 * 24);

    var sameDay =
      start.getUTCFullYear() === end.getUTCFullYear() &&
      start.getUTCMonth() === end.getUTCMonth() &&
      start.getUTCDate() === end.getUTCDate();

    var timeFormat;

    if (sameDay) {
      // same calendar day → time only
      timeFormat = d3.utcFormat("%H:%M:%S");
    } else if (diffDays <= 15) {
      // 2–15 days → date + time
      timeFormat = d3.utcFormat("%d %b %Y");
    } else {
      // >15 days → date only
      timeFormat = d3.utcFormat("%d %b");
    }

    var xAxis = g
      .append("g")
      .attr("class", "xAxis")
      .attr("transform", "translate(0," + innerHeight + ")")
      .call(d3.axisBottom(xScale).ticks(6).tickFormat(timeFormat))
      .append("text")
      .attr("y", 100 - margin)
      .attr("x", innerWidth / 2)
      .style("fill", "white")
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")
      .text(defaultOptiopns.xLabelName);
  };

  const drawYAxis = () => {
    const yAxisOffset =
      (defaultOptiopns.yMaxValue - defaultOptiopns.yMinValue) * 0.1;

    yScale = d3
      .scaleLinear()
      .domain([
        defaultOptiopns.yMinValue,
        defaultOptiopns.yMaxValue + yAxisOffset,
      ])
      .range([innerHeight, 0]);

    var rotate = false;

    var yAxis = g
      .append("g")
      .attr("class", "yAxis")
      .call(
        d3
          .axisLeft(yScale)
          .tickFormat(function (d) {
            if (d.toString().length > 4) {
              rotate = true;
              return d;
            } else return d;
          })
          .ticks(defaultOptiopns.yTicks),
      );

    if (rotate)
      g.selectAll("g.yAxis g.tick")
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "0em")
        .attr("transform", "rotate(-65)");

    g.append("g")
      .attr("class", "yAxisLabel")
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", 30)
      .attr("dy", "-5.5em")
      .style("fill", "white")
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")
      .text(defaultOptiopns.yLabelName);
  };

  /**
   * Draws grid lines aligned with ticks on X and Y axes.
   */
  const drawGrids = () => {
    //Grids
    g.selectAll("g.xAxis g.tick")
      .append("line")
      .attr("class", "gridline")
      .attr("x1", 0)
      .attr("y1", -innerHeight)
      .attr("x2", 0)
      .attr("y2", 0)
      .attr("stroke", "grey")
      .attr("stroke-width", "0.2");

    g.selectAll("g.yAxis g.tick")
      .append("line")
      .attr("class", "gridline")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", innerWidth)
      .attr("y2", 0)
      .attr("stroke", "grey")
      .attr("stroke-width", "0.2");
  };

  const drawDataSplit = () => {
    const segments = splitByTimeGaps(dataset);

    const drawableSegments = segments.filter((s) => s.length > 1);
    const isolatedPoints = segments.filter((s) => s.length === 1).flat();

    const area = d3
      .area()
      .x((d) => xScale(new Date(d.timestamp)))
      .y0(innerHeight)
      .y1((d) => yScale(d.value))
      .curve(d3.curveStep);

    const line = d3
      .line()
      .x((d) => xScale(new Date(d.timestamp)))
      .y((d) => yScale(d.value))
      .curve(d3.curveStep);

    // =========================
    // AREA (only continuous)
    // =========================
    g.selectAll(".area-segment")
      .data(drawableSegments)
      .enter()
      .append("path")
      .attr("class", "area-segment")
      .attr("fill", "red")
      .attr("opacity", 0.4)
      .attr("d", (d) => area(d));

    // =========================
    // LINE (only continuous)
    // =========================
    g.selectAll(".line-segment")
      .data(drawableSegments)
      .enter()
      .append("path")
      .attr("class", "line-segment")
      .attr("fill", "none")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("d", (d) => line(d));

    // =========================
    // DIRAC IMPULSES
    // =========================
    g.selectAll(".dirac")
      .data(isolatedPoints)
      .enter()
      .append("line")
      .attr("class", "dirac")
      .attr("x1", (d) => xScale(new Date(d.timestamp)))
      .attr("x2", (d) => xScale(new Date(d.timestamp)))
      .attr("y1", innerHeight)
      .attr("y2", (d) => yScale(d.value))
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "round");

    // =========================
    // DIRAC CAPS (visual clarity)
    // =========================
    g.selectAll(".dirac-cap")
      .data(isolatedPoints)
      .enter()
      .append("circle")
      .attr("class", "dirac-cap")
      .attr("cx", (d) => xScale(new Date(d.timestamp)))
      .attr("cy", (d) => yScale(d.value))
      .attr("r", 3)
      .attr("fill", "red");
  };

  const drawData = () => {
    const area = d3
      .area()
      .x((d) => xScale(new Date(d.timestamp)))
      .y0(innerHeight)
      .y1((d) => yScale(d.value))
      .curve(d3.curveStep); // smooth curve;

    const line = d3
      .line()
      .x((d) => xScale(new Date(d.timestamp)))
      .y((d) => yScale(d.value))
      .curve(d3.curveStep);

    g.append("path")
      .datum(dataset)
      .attr("fill", "red")
      .attr("opacity", 0.4)
      .attr("d", area);

    g.append("path")
      .datum(dataset)
      .attr("fill", "none")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("d", line);
  };

  /**
   * Draws circles for each data point and attaches tooltip event handlers.
   */
  const drawTooltip = () => {
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "white")
      .style("padding", "6px 10px")
      .style("border", "1px solid #ccc")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-family", "'Open Sans', sans-serif")
      .style("font-size", "12px")
      .style("z-index", "13")
      .style("opacity", 0);

    // Bisector
    const bisect = d3.bisector((d) => new Date(d.timestamp)).left;

    // Focus circle (follows the line)
    const focus = g
      .append("circle")
      .attr("r", 5)
      .attr("fill", "red")
      .style("opacity", 0);

    // Vertical guide line
    const focusLine = g
      .append("line")
      .attr("stroke", "#999")
      .attr("stroke-dasharray", "3,3")
      .style("opacity", 0);

    // Overlay rectangle to capture mouse
    g.append("rect")
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mouseover", () => {
        tooltip.style("opacity", 1);
        focus.style("opacity", 1);
        focusLine.style("opacity", 1);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
        focus.style("opacity", 0);
        focusLine.style("opacity", 0);
      })
      .on("mousemove", function (event) {
        const mouseX = d3.pointer(event)[0];
        const xDate = xScale.invert(mouseX);

        let d;
        if (dataset.length === 1) {
          // 🔥 Special case: only one point
          d = dataset[0];
        } else {
          const i = bisect(dataset, xDate, 1);

          const d0 = dataset[i - 1];
          const d1 = dataset[i];

          if (!d0) d = d1;
          else if (!d1) d = d0;
          else {
            d =
              xDate - new Date(d0.timestamp) > new Date(d1.timestamp) - xDate
                ? d1
                : d0;
          }
        }

        const x = xScale(new Date(d.timestamp));
        const y = yScale(d.value);

        // Move circle
        focus.attr("cx", x).attr("cy", y);

        // Move vertical line
        focusLine
          .attr("x1", x)
          .attr("x2", x)
          .attr("y1", 0)
          .attr("y2", innerHeight);

        // Tooltip content
        tooltip
          .html(
            "<b>Date:</b> " +
              new Date(d.timestamp).toISOString() +
              "<br>" +
              "<b>" +
              defaultOptiopns.yLabelName +
              ":</b> " +
              d.value +
              "<br>",
          )
          .style("top", event.pageY - 10 + "px")
          .style("left", event.pageX + 10 + "px");
      });
  };

  /**
   * Main draw function that initializes SVG and calls sub-draw functions.
   */
  const drawThresholds = () => {
    // Threshold
    g.selectAll(".thresholdLine")
      .data(defaultOptiopns.thresholds)
      .enter()
      .append("line")
      .style("stroke", function (d) {
        return d.color;
      })
      .style("stroke-width", "2")
      .attr("x1", 0)
      .attr("y1", function (d) {
        return yScale(d.value);
      })
      .attr("x2", width)
      .attr("y2", function (d) {
        return yScale(d.value);
      });
  };

  const draw = () => {
    svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height + 20);

    svg
      .append("text")
      .attr("transform", "translate(" + width / 2 + ",0)")
      .attr("y", 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .attr("letter-spacing", "0.054em")
      .text(defaultOptiopns.title);

    drawTimeAxis();
    drawYAxis();
    drawGrids();
    if (dataset && dataset.length > 0) {
      drawAreas();

      drawDataSplit();
      drawTooltip();
    }

    if (defaultOptiopns?.legend?.length > 0) {
      drawLegend();
    }
  };

  return (
    <>
      <div
        className="bar-graph p-2"
        id="container"
        style={{ justifyContent: "center" }}
      >
        <svg
          ref={ref}
          viewBox={`0 0 ${width} ${defaultOptiopns?.legend?.length > 0 ? height + 20 : height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        ></svg>
      </div>
    </>
  );
};

export default AreaGraph;
